import React from "react";
import { FormGroup } from "@blueprintjs/core";
import { ButtonVariant, ButtonView } from "common/views/button/Button.view";
import { IPanelProps, Panel } from "core/decorators/Panel";
import styled from "styled-components";
import labelProvider from "./SaveSearch.labels";
import SaveSearchPanelConfig from "./SaveSearchPanelConfig";
import { FlexJustify, FlexRight } from "common/fragments/Flex";
import { DialogView } from "common/views/dialog/Dialog.view";
import UrlUtil from "core/util/UrlUtil";
import ServiceRegistry from "core/util/ServiceRegistry";
import { ArticleSavedSearch } from "./entities/ArticleSavedSearch";
import StringUtil from "core/util/StringUtil";
import AppToaster from "core/util/AppToaster";

const { Labels } = labelProvider;

interface ISaveSearchPanelState {
	name: string;
	description: string;
	isOpen: boolean;
	id: null;
}

const SearchForm = styled.form`
	display: flex;
	align-items: center;
`;

const InputBlock = styled.div`
	margin-left: 20px;
	margin-top: 10px;
`;

const Save = styled.div`
	margin-right: 20px;
	margin-left: 10px;
`;
const SaveButton = styled.button`
	display: inline-block;
	-webkit-text-decoration: none;
	text-decoration: none;
	color: inherit;
	border: 0;
	-webkit-appearance: none;
	-moz-appearance: none;
	text-align: center;
	height: 25px;
	background-color: rgba(74, 144, 226, 1);
	background-position: center center;
	color: rgba(255, 255, 255, 1);
	border-radius: 2px;
	padding: 0px 10px 0px 10px;
	font-size: 16px;
	cursor: pointer;
	${props =>
		props.disabled &&
		`
    cursor: not-allowed;
  `}
`;

@Panel({
	id: "save-search",
	configClass: SaveSearchPanelConfig,
	pageParams: true,
	labelProvider
})
class SaveSearchPanel extends React.PureComponent<
	IPanelProps<SaveSearchPanelConfig>,
	ISaveSearchPanelState
> {
	public constructor(props: IPanelProps<SaveSearchPanelConfig>) {
		super(props);
		this.state = {
			isOpen: false,
			name: "",
			description: "",
			id: null
		};
	}

	componentDidMount() {
		if (this.props.pageParams["searchid"]) {
			ServiceRegistry.getQueryService()
				.findOne(ArticleSavedSearch, {
					id: StringUtil.toInteger(this.props.pageParams["searchid"])
				})
				.then((result: any) => {
					const data = result.data.entity[0];
					this.setState({
						name: data.name,
						description: data.description,
						id: data.id
					});
				});
		}
	}

	public render() {
		const { name, description, id } = this.state;
		return [
			<SearchForm>
				<ButtonView
					variant={ButtonVariant.PRIMARY}
					onClick={this.openSaveSearchDialog}
					viewKey="open"
				>
					<Labels.Open />
				</ButtonView>
			</SearchForm>,
			<DialogView
				key="dialog"
				isOpen={this.state.isOpen}
				onClose={() => this.setState({ isOpen: false })}
				viewKey="save-dialog"
				title="Save Search"
			>
				<FlexJustify>
					<InputBlock>
						<FormGroup label={<Labels.SearchName />}>
							<input
								value={name}
								style={{
									flexGrow: 1,
									width: "350px",
									height: "30px"
								}}
								onChange={event => this.handleNameChange(event)}
							/>
						</FormGroup>
					</InputBlock>
				</FlexJustify>
				<FlexJustify>
					<InputBlock>
						<FormGroup
							label={<Labels.SearchDescription />}
							helperText="* required"
						>
							<input
								value={description}
								style={{
									flexGrow: 1,
									width: "350px",
									height: "50px"
								}}
								onChange={event =>
									this.handleDescriptionChange(event)
								}
							/>
						</FormGroup>
					</InputBlock>
				</FlexJustify>
				<FlexRight>
					<ButtonView
						variant={ButtonVariant.PRIMARY}
						onClick={() => this.setState({ isOpen: false })}
						viewKey="cancel"
					>
						<Labels.Cancel />
					</ButtonView>
					<Save>
						<SaveButton
							onClick={() => this.onSubmit(id)}
							disabled={!name}
						>
							{!id ? (
								<Labels.SaveSearchData />
							) : (
								<Labels.UpdateSearchData />
							)}
						</SaveButton>
					</Save>
				</FlexRight>
			</DialogView>
		];
	}

	private openSaveSearchDialog = () => {

		if (!this.props.pageParams["searchid"]) {
			this.setState({
				name: null,
				description: null,
				id:null
			});
		}

		this.setState({ isOpen: true });
	};

	private onSubmit = id => {
		if (!id) {
			this.createSearch();
		} else {
			this.updateSearch();
		}
	};

	// Create new save search
	private createSearch() {
		const articleSearch = {
			name: this.state.name,
			description: this.state.description,
			query: window.location.pathname + UrlUtil.getSearch()
		} as any;

		ServiceRegistry.getMutationService()
			.create(articleSearch, ArticleSavedSearch)
			.then(res => {
				this.setState({ isOpen: false, name: "", description: "" });
				AppToaster.show({
					message: "Saved Successfully",
					intent: "success"
				});
			})
			.catch(err => {
				AppToaster.show({
					message: err.message,
					intent: "danger"
				});
			});
	}

	// update saved search
	private updateSearch() {
		const sourceURL = UrlUtil.getSearch();
		const key = "searchid";
		const url = UrlUtil.removeUrlParam(sourceURL, key);

		const articleSearch = {
			id: StringUtil.toInteger(this.state.id),
			name: this.state.name,
			description: this.state.description,
			query: window.location.pathname + url
		} as any;

		ServiceRegistry.getMutationService()
			.update(articleSearch, ArticleSavedSearch)
			.then(res => {
				this.setState({ isOpen: false });
				AppToaster.show({
					message: "Updated Successfully",
					intent: "success"
				});
			})
			.catch(err => {
				AppToaster.show({
					message: err.message,
					intent: "danger"
				});
			});
	}

	/**
	 * input change handler for search name
	 * @param event
	 */
	private handleNameChange = event => {
		this.setState({
			name: event.target.value
		});
	};

	/**
	 * input change handler for search description
	 * @param event
	 */
	private handleDescriptionChange = event => {
		this.setState({
			description: event.target.value
		});
	};
}



// WEBPACK FOOTER //
// ./src/panels/saveSearch/SaveSearch.panel.tsx